import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { ArrowLeft, Save, Mail, Phone } from 'lucide-react';

const EditProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    bio: (user as any)?.bio || '',
  });

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Name is required');
      return;
    }

    try {
      setLoading(true);
      const updateData: any = {
        name: formData.name.trim(),
      };

      if (user?.role === 'provider') {
        if (formData.bio.trim()) {
          updateData.bio = formData.bio.trim();
        }
      }

      await authAPI.updateProfileData(updateData);
      await updateUser(); // Refresh user data
      alert('Profile updated successfully');
      navigate('/profile');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-300 mb-6">You need to be logged in to edit your profile.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center text-black hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Profile
          </button>
          <h1 className="text-3xl font-bold text-white">Edit Profile</h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Read-only Fields */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4">Account Information</h2>
            
            <div className="bg-white rounded-lg p-4 mb-4">
              <div className="flex items-center mb-2">
                <Mail className="h-5 w-5 text-gray-400 mr-3" />
                <label className="text-sm font-medium text-gray-500">Email Address</label>
              </div>
              <p className="text-white ml-8">{user.email}</p>
            </div>

            {user.phone && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Phone className="h-5 w-5 text-gray-400 mr-3" />
                  <label className="text-sm font-medium text-gray-500">Phone Number</label>
                </div>
                <p className="text-gray-900 ml-8">{user.phone}</p>
              </div>
            )}
          </div>

          {/* Editable Fields */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Enter your name"
              />
            </div>

            {user.role === 'provider' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="Tell us about yourself..."
                  rows={4}
                  maxLength={500}
                />
                <p className="text-sm text-gray-500 mt-1 text-right">{formData.bio.length}/500</p>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate('/profile')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfilePage;

